/**
 * Miss Purple – theme.js
 * Expert Shopify theme — Vanilla JS ES6 classes, zéro dépendance
 */

'use strict';

/* ================================================================
   UTILITIES
   ================================================================ */
const formatMoney = cents =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' })
    .format(cents / 100);

const debounce = (fn, ms = 300) => {
  let t;
  return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
};

const trapFocus = (el) => {
  const focusable = el.querySelectorAll(
    'a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])'
  );
  const first = focusable[0];
  const last  = focusable[focusable.length - 1];
  el.addEventListener('keydown', function onKey(e) {
    if (e.key !== 'Tab') return;
    if (e.shiftKey) { if (document.activeElement === first) { e.preventDefault(); last.focus(); } }
    else            { if (document.activeElement === last)  { e.preventDefault(); first.focus(); } }
  });
  first?.focus();
};

/* ================================================================
   HEADER — sticky + scroll hide/show
   ================================================================ */
class SiteHeader {
  constructor() {
    this.el = document.querySelector('.site-header');
    if (!this.el) return;
    this.lastY = 0;
    window.addEventListener('scroll', debounce(() => this.onScroll(), 50), { passive: true });
  }
  onScroll() {
    const y = window.scrollY;
    this.el.classList.toggle('is-scrolled', y > 40);
    this.lastY = y;
  }
}

/* ================================================================
   MOBILE MENU
   ================================================================ */
class MobileMenu {
  constructor() {
    this.btn  = document.querySelector('.js-hamburger');
    this.menu = document.querySelector('.js-mobile-menu');
    if (!this.btn || !this.menu) return;
    this.isOpen = false;
    this.btn.addEventListener('click', () => this.toggle());
    document.addEventListener('keydown', e => e.key === 'Escape' && this.close());
  }
  toggle() { this.isOpen ? this.close() : this.open(); }
  open() {
    this.isOpen = true;
    this.btn.classList.add('is-open');
    this.menu.classList.add('is-open');
    this.menu.setAttribute('aria-hidden', 'false');
    this.btn.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
  }
  close() {
    this.isOpen = false;
    this.btn.classList.remove('is-open');
    this.menu.classList.remove('is-open');
    this.menu.setAttribute('aria-hidden', 'true');
    this.btn.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
  }
}

/* ================================================================
   CART DRAWER — AJAX complet
   ================================================================ */
class CartDrawer {
  constructor() {
    this.drawer   = document.querySelector('.js-cart-drawer');
    this.overlay  = document.querySelector('.js-cart-overlay');
    this.itemsEl  = document.querySelector('[data-cart-items]');
    this.footerEl = document.querySelector('[data-cart-footer]');
    if (!this.drawer) return;
    this.init();
  }

  init() {
    // Ouvrir le tiroir
    document.addEventListener('click', e => {
      if (e.target.closest('[data-open-cart]')) { e.preventDefault(); this.open(); this.refresh(); }
    });
    // Fermer
    this.overlay?.addEventListener('click', () => this.close());
    this.drawer.querySelector('.js-cart-close')?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', e => e.key === 'Escape' && this.isOpen && this.close());
    // Event custom depuis product form
    document.addEventListener('cart:updated', () => this.refresh());
  }

  get isOpen() { return this.drawer.classList.contains('is-open'); }

  open() {
    this.drawer.classList.add('is-open');
    this.overlay?.classList.add('is-active');
    document.body.style.overflow = 'hidden';
    trapFocus(this.drawer);
  }
  close() {
    this.drawer.classList.remove('is-open');
    this.overlay?.classList.remove('is-active');
    document.body.style.overflow = '';
  }

  async refresh() {
    try {
      const cart = await (await fetch('/cart.js')).json();
      this.render(cart);
      this.syncCount(cart.item_count);
    } catch(e) { console.error('CartDrawer.refresh', e); }
  }

  render(cart) {
    if (!this.itemsEl) return;

    this.footerEl && (this.footerEl.style.display = cart.item_count === 0 ? 'none' : '');

    // Total
    const totalEl = this.drawer.querySelector('[data-cart-total]');
    if (totalEl) totalEl.innerHTML = formatMoney(cart.total_price);

    // Barre livraison gratuite
    const FREE_THRESHOLD = 6000; // 60€
    const progress = Math.min(100, (cart.total_price / FREE_THRESHOLD) * 100);
    const remaining = Math.max(0, FREE_THRESHOLD - cart.total_price);
    const shippingBar = remaining > 0
      ? `<div class="shipping-bar"><p class="shipping-bar__msg">Encore <strong>${formatMoney(remaining)}</strong> pour la livraison offerte ✨</p><div class="shipping-bar__track"><div class="shipping-bar__fill" style="width:${progress.toFixed(1)}%"></div></div></div>`
      : `<div class="shipping-bar shipping-bar--done">🎉 Livraison offerte débloquée !</div>`;

    if (cart.item_count === 0) {
      this.itemsEl.innerHTML = `<div class="cart-empty">
        <svg width="64" height="64" viewBox="0 0 64 64" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M8 8h5l7 32h26l5-22H20" stroke-linecap="round" stroke-linejoin="round"/><circle cx="26" cy="54" r="3"/><circle cx="40" cy="54" r="3"/></svg>
        <p>Votre panier est vide</p><a href="/collections/all" class="btn btn--primary" onclick="document.querySelector('.js-cart-drawer').classList.remove('is-open');document.body.style.overflow=''">Découvrir nos bijoux</a></div>`;
      return;
    }

    this.itemsEl.innerHTML = shippingBar + cart.items.map((item, i) => this.renderItem(item, i + 1)).join('');
    this.bindItemEvents();
  }

  renderItem(item, line) {
    const img = item.image ? item.image.replace(/\.(\w+)(\?|$)/, '_100x100.$1$2') : '';
    const varTitle = item.variant_title && item.variant_title !== 'Default Title'
      ? `<p class="cart-item__variant">${item.variant_title}</p>` : '';
    const saving = item.original_line_price > item.final_line_price
      ? `<s class="text-muted">${formatMoney(item.original_line_price)}</s> ` : '';
    return `
      <div class="cart-item" data-line="${line}">
        <a href="${item.url}" class="cart-item__image">${img ? `<img src="${img}" alt="" loading="lazy" width="80" height="80">` : ''}</a>
        <div class="cart-item__body">
          <a href="${item.url}" class="cart-item__title">${item.product_title}</a>
          ${varTitle}
          <div class="cart-item__row">
            <div class="cart-qty" data-line="${line}">
              <button class="cart-qty__btn" data-action="dec" aria-label="Moins">−</button>
              <span class="cart-qty__val">${item.quantity}</span>
              <button class="cart-qty__btn" data-action="inc" aria-label="Plus">+</button>
            </div>
            <span class="cart-item__price">${saving}${formatMoney(item.final_line_price)}</span>
          </div>
          <button class="cart-item__remove" data-line="${line}">Supprimer</button>
        </div>
      </div>`;
  }

  bindItemEvents() {
    this.itemsEl.addEventListener('click', async e => {
      const qBtn = e.target.closest('.cart-qty__btn');
      const rmBtn = e.target.closest('.cart-item__remove');
      if (!qBtn && !rmBtn) return;
      const line = parseInt((qBtn || rmBtn).closest('[data-line]').dataset.line);
      if (qBtn) {
        const valEl = qBtn.closest('.cart-qty').querySelector('.cart-qty__val');
        const qty   = Math.max(0, parseInt(valEl.textContent) + (qBtn.dataset.action === 'inc' ? 1 : -1));
        valEl.textContent = qty;
        await this.changeLine(line, qty);
      }
      if (rmBtn) await this.changeLine(line, 0);
    }, { once: false });
  }

  async changeLine(line, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line, quantity })
    });
    const cart = await res.json();
    this.render(cart);
    this.syncCount(cart.item_count);
  }

  syncCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });
  }
}

/* ================================================================
   PRODUCT GALLERY — thumbnails + crossfade + zoom
   ================================================================ */
class ProductGallery {
  constructor(el) {
    this.el       = el;
    this.mainWrap = el.querySelector('[data-gallery-main]');
    this.mainImg  = el.querySelector('[data-gallery-main] img');
    this.thumbs   = [...el.querySelectorAll('[data-gallery-thumb]')];
    this.idx      = 0;
    if (!this.mainImg) return;
    this.init();
  }

  init() {
    this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.goTo(i)));

    // Zoom
    this.mainWrap?.addEventListener('click',     e => this.toggleZoom(e));
    this.mainWrap?.addEventListener('mousemove', e => { if (this.zoomed) this.moveZoom(e); });
    this.mainWrap?.addEventListener('mouseleave',  () => { if (this.zoomed) this.exitZoom(); });

    // Swipe
    let sx = 0;
    this.mainWrap?.addEventListener('touchstart', e => { sx = e.touches[0].clientX; }, { passive: true });
    this.mainWrap?.addEventListener('touchend',   e => {
      const diff = sx - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 50) diff > 0 ? this.next() : this.prev();
    });

    // Variant image swap
    document.addEventListener('variant:image', e => {
      const mediaId = e.detail?.mediaId;
      if (!mediaId) return;
      const t = this.thumbs.find(t => t.dataset.mediaId == mediaId);
      if (t) this.goTo(this.thumbs.indexOf(t));
    });
  }

  goTo(i) {
    const t = this.thumbs[i];
    if (!t) return;
    this.mainImg.style.opacity = '0';
    setTimeout(() => {
      this.mainImg.src    = t.dataset.src;
      this.mainImg.srcset = t.dataset.srcset || '';
      this.mainImg.alt    = t.dataset.alt    || '';
      this.mainImg.style.opacity = '1';
    }, 180);
    this.thumbs.forEach(th => th.classList.remove('is-active'));
    t.classList.add('is-active');
    this.idx = i;
  }
  next() { this.goTo((this.idx + 1) % (this.thumbs.length || 1)); }
  prev() { this.goTo((this.idx - 1 + (this.thumbs.length || 1)) % (this.thumbs.length || 1)); }

  toggleZoom(e) { this.zoomed ? this.exitZoom() : this.enterZoom(e); }
  enterZoom(e) {
    this.zoomed = true;
    this.mainWrap.classList.add('is-zoomed');
    this.moveZoom(e);
  }
  exitZoom() {
    this.zoomed = false;
    this.mainWrap.classList.remove('is-zoomed');
    this.mainImg.style.transformOrigin = '50% 50%';
  }
  moveZoom(e) {
    const r = this.mainWrap.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width  * 100).toFixed(2);
    const y = ((e.clientY - r.top)  / r.height * 100).toFixed(2);
    this.mainImg.style.transformOrigin = `${x}% ${y}%`;
  }
}

/* ================================================================
   VARIANT PICKER — radio-based, no bugs
   ================================================================ */
class VariantPicker {
  constructor(formEl) {
    this.form    = formEl;
    this.section = formEl.closest('[data-section-id]') || document;

    // Lire les données produit depuis le script tag JSON
    const jsonEl = this.section.querySelector('[data-product-json]');
    if (!jsonEl) return;
    try { this.data = JSON.parse(jsonEl.textContent); }
    catch(e) { console.error('VariantPicker JSON parse error', e); return; }

    this.variants = this.data.variants;
    this.init();
  }

  init() {
    // Écouter les changements de radio
    this.form.addEventListener('change', e => {
      if (e.target.name?.startsWith('options[')) this.update();
    });

    // Boutons +/- quantité
    this.form.addEventListener('click', e => {
      if (e.target.closest('[data-qty-dec]')) this.changeQty(-1);
      if (e.target.closest('[data-qty-inc]')) this.changeQty(1);
    });

    // Soumission
    this.form.addEventListener('submit', e => { e.preventDefault(); this.addToCart(); });

    // Charger variante depuis URL
    const urlVariant = new URLSearchParams(location.search).get('variant');
    if (urlVariant) {
      const v = this.variants.find(v => v.id == urlVariant);
      if (v) {
        v.options.forEach((val, i) => {
          const radio = this.form.querySelector(`input[name="options[${this.data.options[i]}]"][value="${CSS.escape(val)}"]`);
          if (radio) radio.checked = true;
        });
      }
    }

    this.update();
  }

  getSelectedOptions() {
    return this.data.options.map(name => {
      const r = this.form.querySelector(`input[name="options[${name}]"]:checked`);
      return r ? r.value : null;
    });
  }

  getVariant(options) {
    return this.variants.find(v => v.options.every((o, i) => o === options[i])) || null;
  }

  update() {
    const opts    = this.getSelectedOptions();
    const variant = this.getVariant(opts);
    if (!variant) return;

    // Sync hidden input
    const idInput = this.form.querySelector('[name="id"]');
    if (idInput) idInput.value = variant.id;

    // URL
    const url = new URL(location.href);
    url.searchParams.set('variant', variant.id);
    history.replaceState({}, '', url);

    // Prix
    this.updatePrice(variant);
    // Dispo
    this.updateAvailability(variant);
    // Stock urgence
    this.updateStock(variant);
    // Labels des options
    this.updateOptionLabels();
    // Image variante
    if (variant.featured_image?.id) {
      document.dispatchEvent(new CustomEvent('variant:image', { detail: { mediaId: variant.featured_image.id } }));
    }
  }

  updatePrice(v) {
    const w = this.section.querySelector('[data-price-wrapper]');
    if (!w) return;
    const cur  = w.querySelector('[data-price]');
    const comp = w.querySelector('[data-compare-price]');
    const badge= w.querySelector('[data-badge-sale]');
    if (cur) cur.textContent = formatMoney(v.price);
    if (comp) {
      const onSale = v.compare_at_price > v.price;
      comp.textContent = onSale ? formatMoney(v.compare_at_price) : '';
      comp.hidden = !onSale;
      w.classList.toggle('is-on-sale', onSale);
      if (badge) {
        badge.hidden = !onSale;
        if (onSale) badge.textContent = `-${Math.round((1 - v.price / v.compare_at_price) * 100)}%`;
      }
    }
    // Floating bar
    const fp = document.querySelector('[data-floating-price]');
    if (fp) fp.textContent = formatMoney(v.price);
  }

  updateAvailability(v) {
    const btn = this.section.querySelector('[data-add-to-cart]');
    const fb  = document.querySelector('[data-floating-add-to-cart]');
    [btn, fb].filter(Boolean).forEach(b => {
      b.disabled     = !v.available;
      b.textContent  = v.available ? (b.dataset.labelAvailable || 'Ajouter au panier') : 'Épuisé';
    });
  }

  updateStock(v) {
    const el  = this.section.querySelector('[data-stock-alert]');
    if (!el) return;
    const thr = parseInt(this.form.dataset.lowStock || '5');
    const show = v.inventory_management === 'shopify'
      && v.inventory_quantity > 0
      && v.inventory_quantity <= thr;
    el.hidden = !show;
    if (show) el.textContent = `⚡ Plus que ${v.inventory_quantity} en stock !`;
  }

  updateOptionLabels() {
    this.form.querySelectorAll('[data-option-name]').forEach(group => {
      const name    = group.dataset.optionName;
      const checked = group.querySelector(`input[name="options[${name}]"]:checked`);
      const lbl     = group.querySelector('[data-selected-value]');
      if (lbl && checked) lbl.textContent = checked.value;
    });
  }

  changeQty(delta) {
    const input = this.form.querySelector('[name="quantity"]');
    if (!input) return;
    input.value = Math.max(1, parseInt(input.value || 1) + delta);
  }

  async addToCart() {
    const id  = this.form.querySelector('[name="id"]')?.value;
    const qty = parseInt(this.form.querySelector('[name="quantity"]')?.value || 1);
    if (!id) return;

    const btn = this.form.querySelector('[data-add-to-cart]');
    if (btn?.disabled) return;

    if (btn) { btn.disabled = true; btn.dataset.orig = btn.textContent; btn.innerHTML = '<span class="btn-spinner" aria-hidden="true"></span>'; }

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id, quantity: qty })
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.description || 'Erreur'); }

      if (btn) { btn.innerHTML = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><polyline points="2 9 6 13 14 3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg> Ajouté !'; btn.disabled = false; setTimeout(() => { btn.textContent = btn.dataset.orig; }, 2500); }

      document.dispatchEvent(new CustomEvent('cart:updated'));
      showToast('✨ Bijou ajouté au panier !');

      // Ouvrir tiroir
      document.querySelector('.js-cart-drawer')?.classList.add('is-open');
      document.querySelector('.js-cart-overlay')?.classList.add('is-active');
      document.body.style.overflow = 'hidden';

    } catch(err) {
      showToast(err.message, 'error');
      if (btn) { btn.textContent = btn.dataset.orig; btn.disabled = false; }
    }
  }
}

/* ================================================================
   FLOATING CART BAR
   ================================================================ */
class FloatingCartBar {
  constructor() {
    this.bar    = document.querySelector('[data-floating-bar]');
    this.anchor = document.querySelector('[data-add-to-cart]');
    if (!this.bar || !this.anchor) return;

    const obs = new IntersectionObserver(([e]) => {
      this.bar.classList.toggle('is-visible', !e.isIntersecting);
    }, { rootMargin: '0px' });
    obs.observe(this.anchor);

    document.querySelector('[data-floating-add-to-cart]')?.addEventListener('click', () => {
      this.anchor.closest('form')?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    });
  }
}

/* ================================================================
   ACCORDIONS — height CSS animé
   ================================================================ */
class Accordions {
  constructor(root = document) {
    root.querySelectorAll('[data-accordion]').forEach(el => this.bind(el));
  }
  bind(el) {
    const trigger = el.querySelector('[data-accordion-trigger]');
    const content = el.querySelector('[data-accordion-content]');
    if (!trigger || !content) return;
    content.style.height = '0';
    trigger.setAttribute('aria-expanded', 'false');
    trigger.addEventListener('click', () => {
      const open = el.classList.toggle('is-open');
      trigger.setAttribute('aria-expanded', String(open));
      if (open) {
        content.style.height = content.scrollHeight + 'px';
        content.addEventListener('transitionend', () => { content.style.height = 'auto'; }, { once: true });
      } else {
        content.style.height = content.scrollHeight + 'px';
        requestAnimationFrame(() => requestAnimationFrame(() => { content.style.height = '0'; }));
      }
    });
  }
}

/* ================================================================
   HERO SLIDESHOW
   ================================================================ */
class Slideshow {
  constructor(el) {
    this.el     = el;
    this.slides = [...el.querySelectorAll('[data-slide]')];
    this.dots   = [...el.querySelectorAll('[data-dot]')];
    this.idx    = 0;
    this.ms     = parseInt(el.dataset.interval || '5000');
    this.timer  = null;
    if (this.slides.length < 2) return;
    this.init();
  }
  init() {
    this.dots.forEach((d, i) => d.addEventListener('click', () => { this.pause(); this.goTo(i); this.play(); }));
    this.el.querySelector('[data-prev]')?.addEventListener('click', () => { this.pause(); this.prev(); this.play(); });
    this.el.querySelector('[data-next]')?.addEventListener('click', () => { this.pause(); this.next(); this.play(); });
    this.el.addEventListener('mouseenter', () => this.pause());
    this.el.addEventListener('mouseleave', () => this.play());
    let sx = 0;
    this.el.addEventListener('touchstart', e => { sx = e.touches[0].clientX; }, { passive: true });
    this.el.addEventListener('touchend', e => {
      const d = sx - e.changedTouches[0].clientX;
      if (Math.abs(d) > 50) { this.pause(); d > 0 ? this.next() : this.prev(); this.play(); }
    });
    this.goTo(0);
    this.play();
  }
  goTo(i) {
    this.slides[this.idx].classList.remove('is-active');
    this.dots[this.idx]?.classList.remove('is-active');
    this.idx = (i + this.slides.length) % this.slides.length;
    this.slides[this.idx].classList.add('is-active');
    this.dots[this.idx]?.classList.add('is-active');
  }
  next() { this.goTo(this.idx + 1); }
  prev() { this.goTo(this.idx - 1); }
  play()  { this.timer = setInterval(() => this.next(), this.ms); }
  pause() { clearInterval(this.timer); }
}

/* ================================================================
   CAROUSEL — swipeable, responsive
   ================================================================ */
class Carousel {
  constructor(el) {
    this.el    = el;
    this.track = el.querySelector('[data-carousel-track]');
    this.prev  = el.querySelector('[data-carousel-prev]');
    this.next  = el.querySelector('[data-carousel-next]');
    if (!this.track) return;
    this.items = [...this.track.children];
    this.idx   = 0;
    this.init();
  }
  init() {
    this.prev?.addEventListener('click', () => this.go(-1));
    this.next?.addEventListener('click', () => this.go(+1));
    window.addEventListener('resize', debounce(() => this.render(), 200));
    let sx = 0;
    this.track.addEventListener('touchstart', e => { sx = e.touches[0].clientX; }, { passive: true });
    this.track.addEventListener('touchend', e => {
      const d = sx - e.changedTouches[0].clientX;
      if (Math.abs(d) > 50) this.go(d > 0 ? 1 : -1);
    });
    this.render();
  }
  visible() {
    const w = window.innerWidth;
    if (w < 480) return 1; if (w < 768) return 2; if (w < 1024) return 3;
    return parseInt(this.el.dataset.perPage || '4');
  }
  go(dir) {
    const max = Math.max(0, this.items.length - this.visible());
    this.idx  = Math.min(max, Math.max(0, this.idx + dir));
    this.render();
  }
  render() {
    const vis  = this.visible();
    const max  = Math.max(0, this.items.length - vis);
    this.idx   = Math.min(this.idx, max);
    const gap  = parseFloat(getComputedStyle(this.track).columnGap) || 16;
    const w    = this.items[0]?.getBoundingClientRect().width || 0;
    this.track.style.transform = `translateX(-${this.idx * (w + gap)}px)`;
    if (this.prev) this.prev.disabled = this.idx === 0;
    if (this.next) this.next.disabled = this.idx >= max;
  }
}

/* ================================================================
   SCROLL REVEAL
   ================================================================ */
class ScrollReveal {
  constructor() {
    const els = document.querySelectorAll('[data-reveal]');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) { els.forEach(e => e.classList.add('is-revealed')); return; }
    const obs = new IntersectionObserver(entries => {
      entries.forEach(en => { if (en.isIntersecting) { en.target.classList.add('is-revealed'); obs.unobserve(en.target); } });
    }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });
    els.forEach(e => obs.observe(e));
  }
}

/* ================================================================
   PREDICTIVE SEARCH
   ================================================================ */
class PredictiveSearch {
  constructor(el) {
    this.form    = el;
    this.input   = el.querySelector('[data-search-input]');
    this.results = el.querySelector('[data-search-results]');
    if (!this.input || !this.results) return;
    this.input.addEventListener('input', debounce(() => this.search(), 300));
    this.input.addEventListener('focus', () => this.input.value.length >= 2 && this.search());
    document.addEventListener('click', e => !el.contains(e.target) && this.hide());
  }
  async search() {
    const q = this.input.value.trim();
    if (q.length < 2) { this.hide(); return; }
    try {
      const data = await (await fetch(`/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=5`)).json();
      this.render(data.resources?.results?.products || []);
    } catch { this.hide(); }
  }
  render(products) {
    if (!products.length) { this.hide(); return; }
    this.results.innerHTML = products.map(p => `
      <a class="search-item" href="${p.url}">
        ${p.featured_image?.url ? `<img src="${p.featured_image.url}&width=80" alt="" width="40" height="40" loading="lazy">` : '<div class="search-item__placeholder"></div>'}
        <div><span class="search-item__title">${p.title}</span>
        <span class="search-item__price">${p.price_min ? formatMoney(p.price_min) : ''}</span></div>
      </a>`).join('');
    this.results.hidden = false;
  }
  hide() { this.results.hidden = true; }
}

/* ================================================================
   TOAST NOTIFICATION
   ================================================================ */
function showToast(msg, type = 'success') {
  const t = document.querySelector('.toast');
  if (!t) return;
  t.textContent = msg;
  t.className = `toast toast--${type} is-visible`;
  clearTimeout(t._timer);
  t._timer = setTimeout(() => t.classList.remove('is-visible'), 3500);
}

/* ================================================================
   QUICK ADD (depuis les cartes produit)
   ================================================================ */
document.addEventListener('click', async e => {
  const btn = e.target.closest('[data-quick-add]');
  if (!btn) return;
  e.preventDefault();
  const id = btn.dataset.variantId;
  if (!id) return;
  const orig = btn.innerHTML;
  btn.disabled = true;
  btn.innerHTML = '<span class="btn-spinner" aria-hidden="true"></span>';
  try {
    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, quantity: 1 })
    });
    btn.innerHTML = '✓ Ajouté';
    document.dispatchEvent(new CustomEvent('cart:updated'));
    showToast('✨ Ajouté au panier !');
    setTimeout(() => { btn.innerHTML = orig; btn.disabled = false; }, 2500);
  } catch {
    btn.innerHTML = orig; btn.disabled = false;
    showToast('Erreur, réessayez.', 'error');
  }
});

/* ================================================================
   INIT DOMContentLoaded
   ================================================================ */
document.addEventListener('DOMContentLoaded', () => {
  new SiteHeader();
  new MobileMenu();
  new CartDrawer();
  new ScrollReveal();
  new Accordions();

  document.querySelectorAll('[data-slideshow]').forEach(el => new Slideshow(el));
  document.querySelectorAll('[data-carousel]').forEach(el  => new Carousel(el));
  document.querySelectorAll('[data-predictive-search]').forEach(el => new PredictiveSearch(el));

  // Page produit
  const productSection = document.querySelector('[data-section-type="product"]');
  if (productSection) {
    const gallery = productSection.querySelector('[data-product-gallery]');
    const form    = productSection.querySelector('[data-product-form]');
    if (gallery) new ProductGallery(gallery);
    if (form)    new VariantPicker(form);
    new FloatingCartBar();
  }

  // Sync compteur panier au chargement
  fetch('/cart.js').then(r => r.json()).then(cart => {
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = cart.item_count;
      el.style.display = cart.item_count > 0 ? 'flex' : 'none';
    });
  }).catch(() => {});
});
